import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Tab,
  Tabs,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faUpload,
  faImage,
  faEdit,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import CustomSelect from '@src/components/CustomSelect';
import CustomMultiSelect from '@src/components/CustomMultiSelect';
import apis from '@src/apis';
import { COLOR } from '@src/styles/color';
import {
  TOOL_TYPES,
  AI_AGENTS,
  MAX_FILE_SIZE_MB,
  ALLOWED_IMAGE_TYPES,
} from '@src/constants';
import { StyledDetailModal } from './index.style';

const DetailToolModal = ({ open, tool, onClose, onSuccess, appId }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [originalTool, setOriginalTool] = useState(null);

  const [formData, setFormData] = useState({
    code: '',
    type: TOOL_TYPES.AI_TOOL,
    categories: [],
    prompt: '',
    agent: '',
    model: '',
    logoFile: null,
    logoPreview: null,
    iconFile: null,
    iconPreview: null,
    vi: {
      name: '',
      title: '',
      description: '',
      welcomeMessage: '',
      sampleSuggestion1: '',
      sampleSuggestion2: '',
      sampleSuggestion3: '',
    },
    en: {
      name: '',
      title: '',
      description: '',
      welcomeMessage: '',
      sampleSuggestion1: '',
      sampleSuggestion2: '',
      sampleSuggestion3: '',
    },
  });

  const [errors, setErrors] = useState({});

  const fetchCategories = async () => {
    try {
      const response = await apis.category.getCategories();
      if (response.result) {
        const categoriesData = response.result?.data || [];
        setCategories(categoriesData);
      }
    } catch (error) {
      enqueueSnackbar(t('fetchCategoriesError'), { variant: 'error' });
    }
  };

  const populateFormData = (toolData) => {
    const agent = toolData.metadata?.ai?.agent || '';
    setSelectedAgent(agent);

    setFormData({
      code: toolData.code || '',
      type: toolData.type || TOOL_TYPES.AI_TOOL,
      categories: toolData.categories || [],
      prompt: toolData.prompt || '',
      agent,
      model: toolData.metadata?.ai?.model || '',
      logoFile: null,
      logoPreview: toolData.metadata?.assets?.logo || null,
      iconFile: null,
      iconPreview: toolData.icon || null,
      vi: {
        name: toolData.translations?.vi?.name || '',
        title: toolData.translations?.vi?.title || '',
        description: toolData.translations?.vi?.description || '',
        welcomeMessage: toolData.translations?.vi?.welcomeMessage || '',
        sampleSuggestion1: toolData.translations?.vi?.suggestions?.[0] || '',
        sampleSuggestion2: toolData.translations?.vi?.suggestions?.[1] || '',
        sampleSuggestion3: toolData.translations?.vi?.suggestions?.[2] || '',
      },
      en: {
        name: toolData.translations?.en?.name || '',
        title: toolData.translations?.en?.title || '',
        description: toolData.translations?.en?.description || '',
        welcomeMessage: toolData.translations?.en?.welcomeMessage || '',
        sampleSuggestion1: toolData.translations?.en?.suggestions?.[0] || '',
        sampleSuggestion2: toolData.translations?.en?.suggestions?.[1] || '',
        sampleSuggestion3: toolData.translations?.en?.suggestions?.[2] || '',
      },
    });
  };

  const fetchToolDetail = async () => {
    try {
      setLoading(true);

      if (!tool?.id) {
        enqueueSnackbar('Tool ID not found', { variant: 'error' });
        return;
      }

      const response = await apis.toolManagement.getTool(tool.id);
      if (response?.data) {
        populateFormData(response.data);
        setOriginalTool(response.data);
      }
    } catch (error) {
      enqueueSnackbar(t('fetchToolDetailError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value, lang = null) => {
    if (lang) {
      setFormData((prev) => ({
        ...prev,
        [lang]: {
          ...prev[lang],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (errors[field] || (lang && errors[lang]?.[field])) {
      const newErrors = { ...errors };
      if (lang) {
        if (newErrors[lang]) {
          delete newErrors[lang][field];
        }
      } else {
        delete newErrors[field];
      }
      setErrors(newErrors);
    }
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      enqueueSnackbar(t('invalidFileType'), { variant: 'error' });
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      enqueueSnackbar(t('fileSizeExceeded', { size: MAX_FILE_SIZE_MB }), {
        variant: 'error',
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      if (fileType === 'logo') {
        setFormData((prev) => ({
          ...prev,
          logoFile: file,
          logoPreview: event.target.result,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          iconFile: file,
          iconPreview: event.target.result,
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) newErrors.code = t('toolCodeRequired');
    if (!formData.prompt.trim()) newErrors.prompt = t('promptRequired');
    if (formData.categories.length === 0)
      newErrors.categories = t('categoriesRequired');

    if (
      (formData.type === TOOL_TYPES.AI_CHAT ||
        formData.type === TOOL_TYPES.AI_MODEL) &&
      !formData.agent
    ) {
      newErrors.agent = t('agentRequired');
    }
    if (
      (formData.type === TOOL_TYPES.AI_CHAT ||
        formData.type === TOOL_TYPES.AI_MODEL) &&
      !formData.model
    ) {
      newErrors.model = t('modelRequired');
    }

    const viErrors = {};
    if (!formData.vi.name.trim()) viErrors.name = t('nameRequired');
    if (!formData.vi.title.trim()) viErrors.title = t('titleRequired');
    if (!formData.vi.description.trim())
      viErrors.description = t('descriptionRequired');
    if (!formData.vi.welcomeMessage.trim())
      viErrors.welcomeMessage = t('welcomeMessageRequired');

    if (Object.keys(viErrors).length > 0) {
      newErrors.vi = viErrors;
    }

    const enErrors = {};
    if (!formData.en.name.trim()) enErrors.name = t('nameRequired');
    if (!formData.en.title.trim()) enErrors.title = t('titleRequired');
    if (!formData.en.description.trim())
      enErrors.description = t('descriptionRequired');
    if (!formData.en.welcomeMessage.trim())
      enErrors.welcomeMessage = t('welcomeMessageRequired');

    if (Object.keys(enErrors).length > 0) {
      newErrors.en = enErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const prepareFormData = () => {
    const data = new FormData();

    data.append('code', formData.code.trim());
    data.append('type', formData.type);
    data.append('prompt', formData.prompt.trim());
    if (appId) data.append('appId', appId);

    if (formData.agent) data.append('agent', formData.agent);
    if (formData.model) data.append('model', formData.model);

    if (formData.logoFile) data.append('logo', formData.logoFile);
    if (formData.iconFile) data.append('icon', formData.iconFile);

    formData.categories.forEach((categoryId, index) => {
      data.append(`categories[${index}]`, categoryId);
    });

    Object.entries(formData.vi).forEach(([key, value]) => {
      if (value.trim()) {
        data.append(`translations[vi][${key}]`, value.trim());
      }
    });

    Object.entries(formData.en).forEach(([key, value]) => {
      if (value.trim()) {
        data.append(`translations[en][${key}]`, value.trim());
      }
    });

    return data;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const data = prepareFormData();
      const response = await apis.toolManagement.updateTool(tool.id, data);

      if (response?.status) {
        enqueueSnackbar(t('updateToolSuccess'), { variant: 'success' });
        setIsEditing(false);
        await fetchToolDetail();
        onSuccess();
      }
    } catch (error) {
      enqueueSnackbar(t('updateToolError'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isEditing && originalTool) {
      populateFormData(originalTool);
      setIsEditing(false);
      setErrors({});
    } else {
      onClose();
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      setErrors({});
    }
  };

  useEffect(() => {
    if (open && tool) {
      fetchCategories();
      fetchToolDetail();
    }
  }, [open, tool]);

  useEffect(() => {
    if (selectedAgent) {
      const agent = AI_AGENTS.find((a) => a.id === selectedAgent);
      setAvailableModels(agent?.models || []);
    } else {
      setAvailableModels([]);
    }
  }, [selectedAgent]);

  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const agentOptions = AI_AGENTS.map((agent) => ({
    value: agent.id,
    label: agent.name,
  }));

  const modelOptions = availableModels.map((model) => ({
    value: model.id,
    label: model.name,
  }));

  const renderLanguageForm = (lang) => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Typography className="label-input" variant="h6">
          {t('toolName')} <span>*</span>
        </Typography>
        {isEditing ? (
          <TextField
            size="small"
            fullWidth
            value={formData[lang].name}
            onChange={(e) => handleInputChange('name', e.target.value, lang)}
            error={!!errors[lang]?.name}
            helperText={errors[lang]?.name}
          />
        ) : (
          <Box className="view-content">
            <Typography variant="body1" className="view-text">
              {formData[lang].name || '-'}
            </Typography>
          </Box>
        )}
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography className="label-input" variant="h6">
          {t('toolTitle')} <span>*</span>
        </Typography>
        {isEditing ? (
          <TextField
            size="small"
            fullWidth
            value={formData[lang].title}
            onChange={(e) => handleInputChange('title', e.target.value, lang)}
            error={!!errors[lang]?.title}
            helperText={errors[lang]?.title}
          />
        ) : (
          <Box className="view-content">
            <Typography variant="body1" className="view-text">
              {formData[lang].title || '-'}
            </Typography>
          </Box>
        )}
      </Grid>
      <Grid item xs={12}>
        <Typography className="label-input" variant="h6">
          {t('description')} <span>*</span>
        </Typography>
        {isEditing ? (
          <TextField
            size="small"
            fullWidth
            multiline
            rows={3}
            value={formData[lang].description}
            onChange={(e) =>
              handleInputChange('description', e.target.value, lang)
            }
            error={!!errors[lang]?.description}
            helperText={errors[lang]?.description}
          />
        ) : (
          <Box className="view-content view-content-multiline">
            <Typography variant="body1" className="view-text">
              {formData[lang].description || '-'}
            </Typography>
          </Box>
        )}
      </Grid>
      <Grid item xs={12}>
        <Typography className="label-input" variant="h6">
          {t('welcomeMessage')} <span>*</span>
        </Typography>
        {isEditing ? (
          <TextField
            size="small"
            fullWidth
            multiline
            rows={2}
            value={formData[lang].welcomeMessage}
            onChange={(e) =>
              handleInputChange('welcomeMessage', e.target.value, lang)
            }
            error={!!errors[lang]?.welcomeMessage}
            helperText={errors[lang]?.welcomeMessage}
          />
        ) : (
          <Box className="view-content view-content-multiline">
            <Typography variant="body1" className="view-text">
              {formData[lang].welcomeMessage || '-'}
            </Typography>
          </Box>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography className="label-input" variant="h6">
          {t('suggestion')} 1
        </Typography>
        {isEditing ? (
          <TextField
            size="small"
            fullWidth
            value={formData[lang].sampleSuggestion1}
            onChange={(e) =>
              handleInputChange('sampleSuggestion1', e.target.value, lang)
            }
          />
        ) : (
          <Box className="view-content">
            <Typography variant="body1" className="view-text">
              {formData[lang].sampleSuggestion1 || '-'}
            </Typography>
          </Box>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography className="label-input" variant="h6">
          {t('suggestion')} 2
        </Typography>
        {isEditing ? (
          <TextField
            size="small"
            fullWidth
            value={formData[lang].sampleSuggestion2}
            onChange={(e) =>
              handleInputChange('sampleSuggestion2', e.target.value, lang)
            }
          />
        ) : (
          <Box className="view-content">
            <Typography variant="body1" className="view-text">
              {formData[lang].sampleSuggestion2 || '-'}
            </Typography>
          </Box>
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography className="label-input" variant="h6">
          {t('suggestion')} 3
        </Typography>
        {isEditing ? (
          <TextField
            size="small"
            fullWidth
            value={formData[lang].sampleSuggestion3}
            onChange={(e) =>
              handleInputChange('sampleSuggestion3', e.target.value, lang)
            }
          />
        ) : (
          <Box className="view-content">
            <Typography variant="body1" className="view-text">
              {formData[lang].sampleSuggestion3 || '-'}
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );

  return (
    <StyledDetailModal open={open} onClose={onClose} maxWidth="lg">
      <DialogTitle className="dialog-title">
        {t('toolDetails')}
        <Box className="mode-toggle">
          <Button
            variant={isEditing ? 'outlined' : 'contained'}
            size="small"
            onClick={handleEditToggle}
            startIcon={
              <FontAwesomeIcon icon={isEditing ? faEye : faEdit} size="sm" />
            }
          >
            {isEditing ? t('viewMode') : t('editMode')}
          </Button>
          <IconButton onClick={onClose}>
            <FontAwesomeIcon
              icon={faXmark}
              color={COLOR.text.highlight}
              size="sm"
            />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className="dialog-content">
        <Box className="form-section">
          <Typography className="section-title">
            {t('basicInformation')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography className="label-input" variant="h6">
                {t('toolCode')} <span>*</span>
              </Typography>
              {isEditing ? (
                <TextField
                  size="small"
                  fullWidth
                  value={formData.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  error={!!errors.code}
                  helperText={errors.code}
                />
              ) : (
                <Box className="view-content">
                  <Typography variant="body1" className="view-text code-text">
                    {formData.code || '-'}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="label-input" variant="h6">
                {t('toolType')} <span>*</span>
              </Typography>
              {isEditing ? (
                <RadioGroup
                  row
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                >
                  <FormControlLabel
                    value={TOOL_TYPES.AI_TOOL}
                    control={<Radio />}
                    label="AI Tool"
                  />
                  <FormControlLabel
                    value={TOOL_TYPES.AI_CHAT}
                    control={<Radio />}
                    label="AI Chat"
                  />
                  <FormControlLabel
                    value={TOOL_TYPES.AI_MODEL}
                    control={<Radio />}
                    label="AI Model"
                  />
                </RadioGroup>
              ) : (
                <Box className="view-content">
                  <Typography variant="body1" className="view-text">
                    {formData.type === TOOL_TYPES.AI_TOOL && 'AI Tool'}
                    {formData.type === TOOL_TYPES.AI_CHAT && 'AI Chat'}
                    {formData.type === TOOL_TYPES.AI_MODEL && 'AI Model'}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography className="label-input" variant="h6">
                {t('categories')} <span>*</span>
              </Typography>
              {isEditing ? (
                <CustomMultiSelect
                  options={categoryOptions}
                  value={formData.categories}
                  onChange={(value) => handleInputChange('categories', value)}
                  error={!!errors.categories}
                  helperText={errors.categories}
                  placeholder={t('selectCategories')}
                />
              ) : (
                <Box className="view-content">
                  <Typography variant="body1" className="view-text">
                    {formData.categories.length > 0
                      ? formData.categories
                          .map((catId) => {
                            const category = categories.find(
                              (cat) => cat.id === catId,
                            );
                            return category?.name || '-';
                          })
                          .join(', ')
                      : '-'}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography className="label-input" variant="h6">
                {t('prompt')} <span>*</span>
              </Typography>
              {isEditing ? (
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.prompt}
                  onChange={(e) => handleInputChange('prompt', e.target.value)}
                  error={!!errors.prompt}
                  helperText={errors.prompt}
                />
              ) : (
                <Box className="view-content view-content-multiline">
                  <Typography variant="body1" className="view-text">
                    {formData.prompt || '-'}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>

        {(formData.type === TOOL_TYPES.AI_CHAT ||
          formData.type === TOOL_TYPES.AI_MODEL) && (
          <Box className="form-section">
            <Typography className="section-title">
              {t('aiConfiguration')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography className="label-input" variant="h6">
                  {t('agent')} <span>*</span>
                </Typography>
                {isEditing ? (
                  <CustomSelect
                    options={agentOptions}
                    value={selectedAgent}
                    onChange={setSelectedAgent}
                    error={!!errors.agent}
                    helperText={errors.agent}
                  />
                ) : (
                  <Box className="view-content">
                    <Typography variant="body1" className="view-text">
                      {agentOptions.find(
                        (agent) => agent.value === selectedAgent,
                      )?.label || '-'}
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography className="label-input" variant="h6">
                  {t('model')} <span>*</span>
                </Typography>
                {isEditing ? (
                  <CustomSelect
                    options={modelOptions}
                    value={formData.model}
                    onChange={(value) => handleInputChange('model', value)}
                    disabled={!selectedAgent}
                    error={!!errors.model}
                    helperText={errors.model}
                  />
                ) : (
                  <Box className="view-content">
                    <Typography variant="body1" className="view-text">
                      {modelOptions.find(
                        (model) => model.value === formData.model,
                      )?.label || '-'}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        <Box className="form-section">
          <Typography className="section-title">{t('mediaFiles')}</Typography>
          <Box className="file-upload-section">
            <Box className="file-upload-item">
              <Typography className="label-input" variant="h6">
                {t('logo')}
              </Typography>
              <Box
                className="file-preview"
                onClick={() =>
                  isEditing && document.getElementById('logo-upload').click()
                }
                style={{ cursor: isEditing ? 'pointer' : 'default' }}
              >
                {formData.logoPreview ? (
                  <img src={formData.logoPreview} alt="Logo preview" />
                ) : (
                  <FontAwesomeIcon icon={faUpload} color={COLOR.blueGray[64]} />
                )}
              </Box>
              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileChange(e, 'logo')}
                disabled={!isEditing}
              />
            </Box>
            <Box className="file-upload-item">
              <Typography className="label-input" variant="h6">
                {t('icon')}
              </Typography>
              <Box
                className="file-preview"
                onClick={() =>
                  isEditing && document.getElementById('icon-upload').click()
                }
                style={{ cursor: isEditing ? 'pointer' : 'default' }}
              >
                {formData.iconPreview ? (
                  <img src={formData.iconPreview} alt="Icon preview" />
                ) : (
                  <FontAwesomeIcon icon={faImage} color={COLOR.blueGray[64]} />
                )}
              </Box>
              <input
                id="icon-upload"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => handleFileChange(e, 'icon')}
                disabled={!isEditing}
              />
            </Box>
          </Box>
        </Box>

        <Box className="form-section">
          <Typography className="section-title">{t('content')}</Typography>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            className="language-tabs"
          >
            <Tab label={t('vietnamese')} />
            <Tab label={t('english')} />
          </Tabs>
          <Box mt={2}>
            {activeTab === 0 && renderLanguageForm('vi')}
            {activeTab === 1 && renderLanguageForm('en')}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} variant="text">
          {isEditing ? t('cancel') : t('close')}
        </Button>
        {isEditing && (
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? t('updating') : t('update')}
          </Button>
        )}
      </DialogActions>
    </StyledDetailModal>
  );
};

export default DetailToolModal;
